import { PickSnippetArgsData } from '@components/sportsline-pick-snippet/sportsline-pick-snippet';
import { PickSnippetInput, SportslineWebComponent_PickSnippetFragment } from '@lib/graphql-types';
import { PickSnippetEventImpression, PickSnippetEventViewed } from './trackingUtils';
import { isUndefinedOrNull } from './commonUtils';

export const getSnippetValueOrNull = <T>(value: T): T | null => {
  return value ?? null;
};


export const toPickSnippetInput = (data: PickSnippetArgsData): PickSnippetInput => {
  return {
    gameId: data.gameId,
    line: getSnippetValueOrNull(data.line),
    marketId: data.marketId,
    marketName: data.marketName,
    marketType: data.marketType,
    odds: data.odds,
    pick: data.pick,
    playerId: getSnippetValueOrNull(data.playerId),
    playerName: getSnippetValueOrNull(data.playerName),
    teamId: getSnippetValueOrNull(data.teamId?.toString()),
    vendorGameId: data.vendorGameId,
  };
};

export function getEntityType(dataAttr: PickSnippetArgsData | null): 'player' | 'team' | 'standard' {
  if (!isUndefinedOrNull(dataAttr.playerId)) {
    return 'player';
  }
  if (!isUndefinedOrNull(dataAttr.teamId)) {
    return 'team';
  }
  return 'standard'; // Default type if none of the above match
}

type FromPickSnippetAttributeToTrackingImpressionWidgetDataBetsArg = {
  dataAttr: PickSnippetArgsData | null;
  position?: number;
};

export const pickSnippetSides = {
  yes: 'yes',
  no: 'no',
  over: 'over',
  under: 'under',
  home: 'home',
  away: 'away',
  unknown: 'unknown',
} as const;

export const toSides = (side: string = 'unknown'): keyof typeof pickSnippetSides => {
  const lowerCaseSide = side.toLowerCase();
  return lowerCaseSide in pickSnippetSides ? pickSnippetSides[lowerCaseSide] : pickSnippetSides.unknown;
};

export const fromPickSnippetAttributeToTrackingImpressionWidgetDataBets = ({
  dataAttr,
  position = -1
}: FromPickSnippetAttributeToTrackingImpressionWidgetDataBetsArg): PickSnippetEventImpression['widgetData']['bets'][0] => {
  const entityId = dataAttr.playerId ?? dataAttr.teamId ?? null;
  const type = getEntityType(dataAttr);
  return {
    gameId: dataAttr.gameId.toString(),
    position,
    market: {
      id: dataAttr.marketId,
      label: dataAttr.marketName,
    },
    selection: {
      ...(!isUndefinedOrNull(dataAttr.line) && {
        line: dataAttr.line,
      }),
      odds: Number(dataAttr.odds),
      side: toSides(dataAttr.pick?.toLowerCase()),
      // check if has entity
      ...(!isUndefinedOrNull(entityId) && {
        entity: {
          type,
          id: `${entityId}`,
        },
      }),
    },
  };
};

type FromPickSnippetAttributeToTrackingViewedWidgetDataBetsArgs = FromPickSnippetAttributeToTrackingImpressionWidgetDataBetsArg & {
  pick: Pick<SportslineWebComponent_PickSnippetFragment, 'game' | 'selection'>;
};

export const fromPickSnippetAttributeToTrackingViewedWidgetDataBets = ({
  dataAttr,
  pick,
  position  = -1,
}: FromPickSnippetAttributeToTrackingViewedWidgetDataBetsArgs): PickSnippetEventViewed['widgetData']['bets'][0] => {
  const impressionBet = fromPickSnippetAttributeToTrackingImpressionWidgetDataBets({ dataAttr , position });
  return {
    ...impressionBet,
    league: pick.game?.league?.abbrev ?? 'Unknown',
    market: {
      ...impressionBet.market,
      label: pick.selection.marketName,
    },
    selection: {
      ...impressionBet.selection,
      label: pick.selection.labelLong ?? 'Unknown',
    },
  };

};
